import { getElementByCFI } from 'app/services/utils/annotation.utils'
import { markSize, markAreaStep } from 'app/constants/annotation.constants'
import { GroupPosition, MarkArea } from 'app/types/annotation.types'

import positionService from './annotation-position.service'

const getMarkGroupDataId = (bodyId: string, top: number) => `${bodyId}--${top}`

const addGroupMark = (
  coordinates: GroupPosition,
  onClick: (e: MouseEvent, coordinates: GroupPosition) => void,
  isRight: boolean,
  numberOfNotes: number
) => {
  const groupMark = document.createElement('div')
  const mark = getElementByCFI(coordinates.cfi, 'a')
  if (!mark) return

  groupMark.classList.add('group-mark')
  if (isRight) groupMark.classList.add('right')
  groupMark.setAttribute('data-numberOfNotes', `${numberOfNotes}`)
  groupMark.style.top = `${coordinates.top}px`
  groupMark.style.left = `${coordinates.left}px`
  groupMark.dataset.id = getMarkGroupDataId(
    coordinates.id ?? '',
    +coordinates.top
  )
  groupMark.addEventListener('click', (e) => onClick(e, coordinates))

  mark.parentNode?.appendChild(groupMark)
}

const removeGroupMark = (mark: Element | null) => {
  if (mark) mark.parentNode?.removeChild(mark)
}

const getCurrentGroupMark = (id: string, container = document) =>
  container.querySelector(`div.group-mark[data-id='${id}']`)

const updateNumberOfNotes = (
  groupMarkButton: Element | null,
  numberOfNotes: number
) => {
  groupMarkButton?.setAttribute('data-numberOfNotes', `${numberOfNotes}`)
}

const buildMarkArea = (container: Element | null) => {
  const markArea: Record<string, { lineTop: number }> = {}

  if (!container) return markArea

  const containerRect = container.getBoundingClientRect()
  const startPosition = containerRect.top - markSize / 2

  for (
    let lineTop = startPosition;
    lineTop <= containerRect.height;
    lineTop += markAreaStep
  ) {
    // debug
    // const line = document.createElement('div')
    // line.classList.add('line')
    // line.style.top = `${lineTop}px`
    // container.appendChild(line)
    // debug
    markArea[lineTop] = { lineTop }
  }

  return markArea
}

const clearMarkArea = (markArea: MarkArea) => {
  const clearedArea: Record<string, { lineTop: number }> = {}
  Object.keys(markArea).forEach((key) => {
    clearedArea[key] = { lineTop: +key }
  })

  return clearedArea
}

const findClosestLine = (
  markCenter: number,
  markArea: Record<string, { lineTop: number }>
) => {
  const firstMarkAreaKey = Object.keys(markArea)[0]

  let closest = {
    lineTop: markArea[firstMarkAreaKey].lineTop,
    distance: 500,
  }

  Object.keys(markArea).forEach((lineTop) => {
    const distance = Math.abs(+lineTop - markCenter)
    if (distance < closest.distance) closest = { distance, lineTop: +lineTop }
  })

  return closest.lineTop
}

const getOverlap = (
  markTop: number,
  nearAreaTop: number,
  nearAreaMarks: { cfi: string }[]
) => {
  if (!nearAreaMarks || !nearAreaMarks.length) return 0

  let nearMarkTop = nearAreaTop
  if (nearAreaMarks.length === 1) {
    nearMarkTop = positionService.getSettedMarkPosition(
      nearAreaMarks[0].cfi
    ).top
  }

  const isBottomOverlap = nearMarkTop > markTop

  const intersaction = isBottomOverlap
    ? markTop + markSize - nearMarkTop
    : nearMarkTop + markSize - markTop

  if (intersaction > 0) return isBottomOverlap ? -intersaction : intersaction
  return 0
}

const findOverlap = (
  top: number,
  areaTop: number,
  markArea: MarkArea,
  bodyId: string
) => {
  const prevTop = +areaTop - markAreaStep
  const nextTop = +areaTop + markAreaStep

  const prevAreaMarks = markArea[prevTop]?.[bodyId]?.marks
  const nextAreaMarks = markArea[nextTop]?.[bodyId]?.marks

  // Mark can overlap just at the top or at the bottom
  return (
    getOverlap(top, prevTop, prevAreaMarks) ||
    getOverlap(top, nextTop, nextAreaMarks) ||
    0
  )
}

// Find overlap of group mark with singe mark
const findGroupOverlap = (
  areaTop: string,
  markArea: MarkArea,
  bodyId: string
) => {
  const prevTop = +areaTop - markAreaStep
  const nextTop = +areaTop + markAreaStep

  const prevAreaMarks = markArea[prevTop]?.[bodyId]?.marks
  const nextAreaMarks = markArea[nextTop]?.[bodyId]?.marks

  // If there are overlap, we move single mark, not group
  const prevOverlap = getOverlap(+areaTop, prevTop, prevAreaMarks)
  if (prevOverlap) {
    return { distance: -prevOverlap, markCfi: prevAreaMarks[0].cfi }
  }

  const nextOverlap = getOverlap(+areaTop, nextTop, nextAreaMarks)
  if (nextOverlap) {
    return { distance: -nextOverlap, markCfi: nextAreaMarks[0].cfi }
  }

  return 0
}

export default {
  getMarkGroupDataId,
  addGroupMark,
  removeGroupMark,
  getCurrentGroupMark,
  updateNumberOfNotes,
  buildMarkArea,
  clearMarkArea,
  findClosestLine,
  findOverlap,
  findGroupOverlap,
}
